import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, SettingsIcon, RefreshCwIcon, EyeIcon, ToggleLeft, ToggleRight, Search, Loader2, X, ChevronDown, Sparkles, History, User2, UserCircle, Ghost } from 'lucide-react';
import { coreServicesApi, type JsonValue } from '../lib/api/core-services.js';
import { PKBConfigPage } from '../components/PKBConfigPage.js';
import { handleApiError, type ApiError } from '../lib/api/client.js';
import { useDiscordGuilds, useDiscordGuildMembers } from '../hooks/usePermissions.js';
import { getStatusBadgeClass } from '../lib/statusUtils.js';
import { BotPageLayout } from '../components/shared/BotPageLayout.js';
import { getDiscordAvatarUrl } from '../lib/discord-utils.js';
import type { DiscordMember } from '../lib/api/types.js';
import type { PKBImageEvent } from '../lib/api/domains/praise-kink.js';
import { Button, Card, Alert, Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem, Badge, Tooltip, TooltipTrigger, TooltipContent } from '@randompedestrian07/ui';
import clsx from 'clsx';

const SENSITIVITY_OPTIONS: Array<{ id: 'strict' | 'moderate' | 'permissive'; label: string }> = [
  { id: 'strict', label: 'Strict' },
  { id: 'moderate', label: 'Moderate' },
  { id: 'permissive', label: 'Permissive' },
];

export const PKBPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'events' | 'personal' | 'configuration'>('events');
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [selectedGuild, setSelectedGuild] = useState<string>('');
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState<DiscordMember | null>(null);
  const [modalPromptText, setModalPromptText] = useState('');
  const [modalEnabled, setModalEnabled] = useState(true);
  const [promptHistory, setPromptHistory] = useState<Array<{ text: string; createdAt: string | null }>>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());

  const { data: guildsData, isLoading: guildsLoading } = useDiscordGuilds();
  const { data: guildMembersData, isLoading: membersLoading, refetch: refetchMembers } = useDiscordGuildMembers(
    selectedGuild,
    debouncedSearch,
    !!selectedGuild
  );

  useEffect(() => {
    if (!selectedGuild && guildsData?.guilds?.length) {
      setSelectedGuild(guildsData.guilds[0].id);
    }
  }, [guildsData, selectedGuild]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(memberSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [memberSearch]);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['pkb', 'config'],
    queryFn: () => coreServicesApi.praiseKink.getConfig(),
  });

  const setConfig = useMutation({
    mutationFn: (params: { key: string; value: JsonValue }) => coreServicesApi.praiseKink.setConfig(params.key, params.value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pkb', 'config'] });
    },
  });

  const { data: imageEvents, isLoading: eventsLoading, refetch: refetchEvents } = useQuery({
    queryKey: ['pkb', 'image-events'],
    queryFn: () => coreServicesApi.praiseKink.listImageEvents(20, 0),
  });

  const isSavingConfig = setConfig.isPending;
  const loggingEnabled =
    config?.enableImageEventLogging === true || String(config?.enableImageEventLogging) === 'true';
  const directLewdEnabled =
    config?.directLewdEnabled === true || String(config?.directLewdEnabled) === 'true';

  const { data: userPrompts, refetch: refetchUserPrompts } = useQuery({
    queryKey: ['pkb', 'user-prompts'],
    queryFn: () => coreServicesApi.praiseKink.listUserPrompts(),
  });

  const userPromptMap = useMemo(() => {
    const map = new Map<string, { text: string | null; isEnabled: boolean; createdAt?: string | null; updatedAt?: string | null }>();
    (userPrompts?.prompts || []).forEach((prompt) => {
      map.set(prompt.userId, prompt);
    });
    return map;
  }, [userPrompts]);

  const members = (guildMembersData?.members || []).filter((m) => !m.bot);

  const setUserPrompt = useMutation({
    mutationFn: ({ userId, text, enabled }: { userId: string; text?: string; enabled?: boolean }) =>
      coreServicesApi.praiseKink.setUserPrompt(userId, text, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pkb', 'user-prompts'] });
    },
    onError: (err: unknown) => {
      alert(handleApiError(err as ApiError));
    },
  });

  const deleteUserPrompt = useMutation({
    mutationFn: (userId: string) => coreServicesApi.praiseKink.deleteUserPrompt(userId),
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['pkb', 'user-prompts'] });
      if (selectedMember?.id === userId) {
        setSelectedMember(null);
      }
    },
  });

  const openUserPromptModal = async (member: DiscordMember) => {
    setSelectedMember(member);
    setShowHistory(false);
    setPromptHistory([]);
    setHistoryLoading(true);
    try {
      const data = await coreServicesApi.praiseKink.getUserPrompt(member.id);
      setModalPromptText(data.text || '');
      setModalEnabled(data.isEnabled ?? true);
      setPromptHistory(data.history || []);
    } catch {
      const prompt = userPromptMap.get(member.id);
      setModalPromptText(prompt?.text || '');
      setModalEnabled(prompt?.isEnabled ?? true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeUserPromptModal = () => {
    setSelectedMember(null);
    setModalPromptText('');
    setPromptHistory([]);
    setShowHistory(false);
  };

  const handleTogglePrompt = (userId: string, enable: boolean) => {
    const prompt = userPromptMap.get(userId);
    if (!prompt) {
      alert('Set a prompt first, then you can enable/disable it.');
      return;
    }
    setUserPrompt.mutate({ userId, text: prompt.text ?? undefined, enabled: enable });
  };

  const handleSaveModalPrompt = async () => {
    if (!selectedMember) return;
    const trimmed = modalPromptText.trim();
    if (!trimmed) {
      alert('Prompt text cannot be empty.');
      return;
    }
    try {
      await setUserPrompt.mutateAsync({ userId: selectedMember.id, text: trimmed, enabled: modalEnabled });
      closeUserPromptModal();
    } catch (err) {
      alert(handleApiError(err as ApiError));
    }
  };

  const deleteImageEvent = useMutation({
    mutationFn: async (messageId: string) => {
      const confirmed = window.confirm('Delete this image event permanently? This cannot be undone.');
      if (!confirmed) return;
      await coreServicesApi.praiseKink.deleteImageEvent(messageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pkb', 'image-events'] });
      setSelectedEvents(new Set());
    },
    onError: (err: unknown) => {
      alert(handleApiError(err as ApiError));
    }
  });

  const deleteSelectedEvents = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const confirmed = window.confirm(`Delete ${messageIds.length} image events permanently? This cannot be undone.`);
      if (!confirmed) return;
      await Promise.all(messageIds.map(id => coreServicesApi.praiseKink.deleteImageEvent(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pkb', 'image-events'] });
      setSelectedEvents(new Set());
    },
    onError: (err: unknown) => {
      alert(handleApiError(err as ApiError));
    }
  });

  const toggleEventSelection = (messageId: string) => {
    setSelectedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const toggleAllEvents = () => {
    if (selectedEvents.size === imageEvents?.length) {
      setSelectedEvents(new Set());
    } else {
      setSelectedEvents(new Set(imageEvents?.map(e => e.messageId) || []));
    }
  };

  const getAnalysisRecord = (analysis: PKBImageEvent['analysis']): Record<string, JsonValue> | null => {
    if (!analysis || typeof analysis !== 'object' || Array.isArray(analysis)) return null;
    return analysis as Record<string, JsonValue>;
  };

  const getAnalysisString = (analysis: PKBImageEvent['analysis'], key: string): string | undefined => {
    const record = getAnalysisRecord(analysis);
    const value = record?.[key];
    return typeof value === 'string' ? value : undefined;
  };

  const getAnalysisBoolean = (analysis: PKBImageEvent['analysis'], key: string): boolean | undefined => {
    const record = getAnalysisRecord(analysis);
    const value = record?.[key];
    return typeof value === 'boolean' ? value : undefined;
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['pkb', 'stats'],
    queryFn: () => coreServicesApi.praiseKink.getStats(),
    refetchInterval: 30000,
  });

  const tabs = [
    { id: 'events', label: 'Events', icon: <ImageIcon size={16} /> },
    { id: 'personal', label: 'Personal', icon: <UserCircle size={16} /> },
    { id: 'configuration', label: 'Configuration', icon: <SettingsIcon size={16} /> },
  ];

  const statusBadges = stats ? [{ label: 'BOT', status: stats.status }] : [];

  return (
    <BotPageLayout
      icon={<ImageIcon className="text-accent" size={32} />}
      iconColorClass="bg-accent/10 shadow-accent/10"
      title="Praise Kink Bot"
      statusBadges={statusBadges}
      isLoading={statsLoading}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as 'events' | 'personal' | 'configuration')}
    >
      {activeTab === 'configuration' && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PKBConfigPage />
          <Card className="p-8 border-stroke bg-panel/20 backdrop-blur-md rounded-3xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Sparkles size={120} />
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
              <div>
                <h3 className="text-[10px] font-bold uppercase tracking-widest text-muted/60 mb-2">Sensitivity & Logging</h3>
                <h4 className="text-2xl font-bold text-text tracking-tight">Additional Constraints</h4>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-xs font-bold text-muted/80 px-1 italic">Moderation Sensitivity</label>
                <Select
                  value={(config?.sensitivity as string) || 'moderate'}
                  onValueChange={(value) => setConfig.mutate({ key: 'sensitivity', value })}
                >
                  <SelectTrigger className="bg-panel-strong/40 border-stroke h-12 rounded-2xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SENSITIVITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id} className="rounded-xl">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted italic px-1 opacity-60">Higher sensitivity means stricter filtering of lewd content.</p>
              </div>

              <div className="bg-panel-strong/40 border border-stroke p-6 rounded-2xl shadow-inner flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-text mb-1">Image Event Logging</div>
                  <div className="text-[10px] text-muted font-medium pr-8">Archive analysis metadata, URLs, and AI responses for debugging.</div>
                </div>
                <button
                  className={clsx(
                    "relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 shadow-lg",
                    loggingEnabled ? "bg-green shadow-green/20" : "bg-panel-soft shadow-inner"
                  )}
                  onClick={() =>
                    setConfig.mutate({
                      key: 'enableImageEventLogging',
                      value: !loggingEnabled,
                    })
                  }
                  disabled={configLoading || isSavingConfig}
                >
                  <span
                    className={clsx(
                      "inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-all duration-300",
                      loggingEnabled ? "translate-x-7" : "translate-x-1"
                    )}
                  />
                </button>
              </div>

              <div className="bg-panel-strong/40 border border-stroke p-6 rounded-2xl shadow-inner flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-text mb-1 flex items-center gap-2">
                    Direct Lewd Mode
                    <Badge variant="outline" className="text-[9px] border-accent/30 text-accent px-1.5 py-0 uppercase font-bold">Vision</Badge>
                  </div>
                  <div className="text-[10px] text-muted font-medium pr-8">Single uncensored vision call for lewd images (uses the Direct Lewd prompt config). Skips the separate describe step.</div>
                </div>
                <button
                  className={clsx(
                    "relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 shadow-lg",
                    directLewdEnabled ? "bg-green shadow-green/20" : "bg-panel-soft shadow-inner"
                  )}
                  onClick={() =>
                    setConfig.mutate({
                      key: 'directLewdEnabled',
                      value: !directLewdEnabled,
                    })
                  }
                  disabled={configLoading || isSavingConfig}
                >
                  <span
                    className={clsx(
                      "inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-all duration-300",
                      directLewdEnabled ? "translate-x-7" : "translate-x-1"
                    )}
                  />
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'events' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 px-1">
            <div>
              <h2 className="text-2xl font-bold text-text tracking-tight flex items-center gap-3">
                <EyeIcon className="text-accent" size={24} />
                Recent Image Events
              </h2>
              <p className="text-xs text-muted font-medium mt-1">Audit and manage intercepted image content.</p>
            </div>
            <div className="flex items-center gap-3">
              {imageEvents && imageEvents.length > 0 && (
                <div className="flex bg-panel-strong/40 p-1 rounded-xl border border-stroke items-center gap-2 px-3">
                  <label className="flex items-center text-[10px] font-bold uppercase tracking-widest text-muted cursor-pointer hover:text-text transition-colors">
                    <input
                      type="checkbox"
                      className="mr-2 w-4 h-4 rounded-md border-stroke bg-panel text-accent accent-accent focus:ring-accent/20"
                      checked={selectedEvents.size === imageEvents.length && imageEvents.length > 0}
                      onChange={toggleAllEvents}
                    />
                    Select All
                  </label>
                  {selectedEvents.size > 0 && (
                    <>
                      <div className="w-px h-4 bg-stroke mx-1" />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[10px] font-bold text-muted hover:text-text"
                        onClick={() => setSelectedEvents(new Set())}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-7 text-[10px] font-bold bg-red/10 text-red border-red/20"
                        onClick={() => deleteSelectedEvents.mutate(Array.from(selectedEvents))}
                      >
                        <X size={12} className="mr-1" />
                        Delete {selectedEvents.size}
                      </Button>
                    </>
                  )}
                </div>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="h-9 font-bold text-xs bg-panel-strong/40 border-stroke"
                onClick={() => refetchEvents()}
              >
                <RefreshCwIcon size={14} className={clsx("mr-2", eventsLoading && "animate-spin")} />
                Sync
              </Button>
            </div>
          </div>

          {expandedImage && (
            <div
              className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-8 backdrop-blur-sm animate-in fade-in duration-300"
              onClick={() => setExpandedImage(null)}
            >
              <img
                src={expandedImage}
                alt="Full size"
                className="max-h-full max-w-full object-contain rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10"
              />
              <button className="absolute top-8 right-8 text-white/40 hover:text-white transition-colors bg-black/40 p-2 rounded-full border border-white/10">
                <X size={32} />
              </button>
            </div>
          )}

          {eventsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-[400px] rounded-3xl bg-panel-strong/20 border border-stroke animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {imageEvents && imageEvents.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                  {imageEvents.map((event) => {
                    const isLewd = Boolean(getAnalysisBoolean(event.analysis, 'isLewd'));
                    const visionProvider = getAnalysisString(event.analysis, 'visionProvider');
                    const visionModel = getAnalysisString(event.analysis, 'visionModel');
                    const responseProvider = getAnalysisString(event.analysis, 'responseProvider') || getAnalysisString(event.analysis, 'provider');
                    const responseModel = getAnalysisString(event.analysis, 'responseModel');

                    return (
                      <Card
                        key={event.messageId}
                        className={clsx(
                          "group relative overflow-hidden transition-all duration-500 border-stroke bg-panel/30 backdrop-blur-md rounded-3xl",
                          isLewd && "ring-1 ring-red/30 shadow-2xl shadow-red/5 bg-red/[0.02]",
                          selectedEvents.has(event.messageId) && "ring-2 ring-accent shadow-2xl shadow-accent/10 bg-accent/[0.02]"
                        )}
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-panel-strong flex items-center justify-center border-b border-stroke">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 opacity-60" />
                          <ImagePreview imageUrl={event.imageUrl} onExpand={(url) => setExpandedImage(url)} />

                          <div className="absolute top-4 left-4 z-20">
                            <input
                              type="checkbox"
                              className="w-5 h-5 rounded-lg border-stroke bg-black/40 text-accent accent-accent cursor-pointer transition-transform hover:scale-110"
                              checked={selectedEvents.has(event.messageId)}
                              onChange={() => toggleEventSelection(event.messageId)}
                            />
                          </div>

                          <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 items-end">
                            {isLewd && (
                              <Badge className="bg-red/10 text-red border-red/20 backdrop-blur-md font-black italic tracking-tighter text-[10px] px-2 py-0.5 animate-pulse">
                                LEWD CONTENT
                              </Badge>
                            )}
                            <button
                              onClick={() => deleteImageEvent.mutate(event.messageId)}
                              className="p-2 rounded-xl bg-black/40 backdrop-blur-md border border-white/5 text-white/40 hover:text-red hover:bg-red/10 transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="absolute bottom-4 left-4 z-20">
                            <span className="text-[10px] font-bold text-white/60 tracking-widest uppercase truncate max-w-[200px] block">
                              MSG: {event.messageId}
                            </span>
                            <span className="text-[9px] font-bold text-white/40 uppercase block mt-0.5">
                              {new Date(event.createdAt).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        <div className="p-6 space-y-5">
                          <div className="flex flex-wrap gap-2">
                            {visionProvider && (
                              <div className="flex flex-col">
                                <span className="text-[8px] font-black uppercase text-muted/40 tracking-widest ml-1 mb-1">Vision Engine</span>
                                <Badge variant="secondary" className="bg-panel-strong/60 border-stroke text-[10px] py-0 px-2 h-6 flex items-center gap-1.5 font-bold">
                                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                                  {visionProvider}
                                </Badge>
                              </div>
                            )}
                            {responseProvider && (
                              <div className="flex flex-col">
                                <span className="text-[8px] font-black uppercase text-muted/40 tracking-widest ml-1 mb-1">Response</span>
                                <Badge variant="secondary" className="bg-panel-strong/60 border-stroke text-[10px] py-0 px-2 h-6 flex items-center gap-1.5 font-bold">
                                  <div className="w-1.5 h-1.5 rounded-full bg-green" />
                                  {responseProvider}
                                </Badge>
                              </div>
                            )}
                          </div>

                          <div className="space-y-4">
                            <AccordionRow title="Metadata & Analysis" defaultOpen={false}>
                              <div className="bg-panel-strong rounded-2xl p-4 border border-stroke shadow-inner overflow-hidden">
                                <pre className="text-[11px] leading-relaxed text-text/70 scrollbar-thin scrollbar-thumb-stroke font-mono overflow-x-auto max-h-60">
                                  {event.analysis ? JSON.stringify(event.analysis, null, 2) : '— No analysis available —'}
                                </pre>
                              </div>
                            </AccordionRow>

                            <AccordionRow title="AI Interaction" defaultOpen={false}>
                              <div className="bg-panel-strong rounded-2xl p-4 border border-stroke shadow-inner">
                                <p className="text-sm font-medium text-text/80 leading-relaxed italic">
                                  "{event.botResponse || '— No response captured —'}"
                                </p>
                              </div>
                            </AccordionRow>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="py-24 flex flex-col items-center justify-center text-muted border-2 border-dashed border-stroke rounded-[40px] opacity-40">
                  <Ghost size={48} className="mb-4" />
                  <p className="font-bold uppercase tracking-widest text-xs">The archive is empty</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'personal' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="p-8 border-stroke bg-panel/30 backdrop-blur-md rounded-[2rem] shadow-2xl relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <User2 size={120} />
            </div>
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 relative z-10">
              <div className="space-y-1">
                <h2 className="text-2xl font-bold text-text tracking-tight flex items-center gap-3">
                  <User2 className="text-accent" size={24} />
                  Personal Overrides
                </h2>
                <p className="text-xs text-muted font-medium italic">Custom prompts for individual users.</p>
              </div>
              <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 w-full lg:w-auto">
                <div className="flex flex-col gap-1.5 min-w-[240px]">
                  <span className="text-[9px] font-black uppercase tracking-widest text-muted/60 ml-1">Server</span>
                  <Select
                    value={selectedGuild}
                    onValueChange={(value) => setSelectedGuild(value)}
                  >
                    <SelectTrigger className="bg-panel-strong/40 border-stroke h-11 rounded-xl">
                      <SelectValue placeholder="Select server..." />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-stroke bg-panel-strong/60 backdrop-blur-xl">
                      {(guildsData?.guilds || []).map((guild) => (
                        <SelectItem key={guild.id} value={guild.id} className="rounded-xl">
                          {guild.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1.5 flex-1 md:min-w-[320px]">
                  <span className="text-[9px] font-black uppercase tracking-widest text-muted/60 ml-1">Search</span>
                  <div className="relative">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted/40" />
                    <Input
                      type="text"
                      placeholder="Username, Display Name, or Hash ID..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      disabled={!selectedGuild}
                      className="pl-11 pr-11 bg-panel-strong/40 border-stroke h-11 rounded-xl placeholder:text-muted/30 focus:ring-accent/20"
                    />
                    {memberSearch && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-muted hover:text-text"
                        onClick={() => setMemberSearch('')}
                      >
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-end h-11">
                  <Button
                    variant="secondary"
                    className="h-11 px-4 bg-panel-strong/40 border-stroke hover:bg-panel font-bold text-xs"
                    onClick={() => {
                      refetchUserPrompts();
                      refetchMembers();
                    }}
                  >
                    <RefreshCwIcon size={14} className={clsx(membersLoading && "animate-spin")} />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <div className="space-y-6">
            <div className="flex items-center justify-between px-2">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted/60">
                Users ({members.length})
              </h3>
            </div>

            {guildsLoading ? (
              <div className="py-24 flex flex-col items-center justify-center text-muted gap-4">
                <Loader2 className="animate-spin text-accent" size={32} />
                <p className="text-xs font-bold uppercase tracking-widest animate-pulse">Loading...</p>
              </div>
            ) : !selectedGuild ? (
              <div className="py-32 flex flex-col items-center justify-center text-muted border-2 border-dashed border-stroke rounded-[3rem] opacity-30">
                <UserCircle size={48} className="mb-4" />
                <p className="font-bold uppercase tracking-widest text-xs">Select a server to view users</p>
              </div>
            ) : membersLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-24 rounded-3xl bg-panel-strong/20 border border-stroke animate-pulse" />
                ))}
              </div>
            ) : members.length === 0 ? (
              <div className="py-32 flex flex-col items-center justify-center text-muted border-2 border-dashed border-stroke rounded-[3rem] opacity-30">
                <Search size={48} className="mb-4" />
                <p className="font-bold uppercase tracking-widest text-xs">No users found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {members.map((member) => {
                  const prompt = userPromptMap.get(member.id);
                  const hasPrompt = !!prompt;
                  const isEnabled = prompt?.isEnabled !== false;

                  return (
                    <Card
                      key={member.id}
                      className={clsx(
                        "group p-5 border-stroke bg-panel/30 backdrop-blur-md rounded-[1.75rem] transition-all duration-300 hover:shadow-2xl hover:shadow-accent/5 hover:-translate-y-1",
                        hasPrompt && isEnabled && "ring-1 ring-green/20 bg-green/[0.02]"
                      )}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <img
                              src={getDiscordAvatarUrl(member.id, member.avatar)}
                              alt={member.username}
                              className="w-12 h-12 rounded-2xl border border-stroke object-cover shadow-lg"
                            />
                            {hasPrompt && (
                              <div className={clsx(
                                "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-bg-solid shadow-sm animate-pulse",
                                isEnabled ? "bg-green" : "bg-muted"
                              )} />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-text leading-tight">{member.displayName || member.username}</div>
                            <div className="text-[10px] font-bold text-muted/40 uppercase tracking-widest truncate max-w-[120px]">@{member.username}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {hasPrompt && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleTogglePrompt(member.id, !isEnabled)}
                                  className={clsx(
                                    "p-2 rounded-xl border transition-all duration-300",
                                    isEnabled ? "bg-green/10 border-green/20 text-green" : "bg-panel-soft border-stroke text-muted"
                                  )}
                                >
                                  {isEnabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>{isEnabled ? 'Enabled' : 'Disabled'}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>

                      <div className="mt-6 flex flex-col gap-2">
                        <Button
                          variant={hasPrompt ? "secondary" : "default"}
                          size="sm"
                          className={clsx(
                            "w-full h-10 font-bold text-[11px] uppercase tracking-widest rounded-xl transition-all",
                            hasPrompt ? "bg-panel-strong/40 border-stroke border" : "shadow-lg shadow-accent/20"
                          )}
                          onClick={() => openUserPromptModal(member)}
                        >
                          {hasPrompt ? 'Edit Prompt' : 'Add Prompt'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedMember && (
        <UserPromptModal
          member={selectedMember}
          prompt={userPromptMap.get(selectedMember.id)}
          promptText={modalPromptText}
          isEnabled={modalEnabled}
          history={promptHistory}
          historyLoading={historyLoading}
          showHistory={showHistory}
          onChangePrompt={setModalPromptText}
          onToggleEnabled={setModalEnabled}
          onToggleHistory={setShowHistory}
          onClose={closeUserPromptModal}
          onSave={handleSaveModalPrompt}
          onDelete={() => deleteUserPrompt.mutate(selectedMember.id)}
          isSaving={setUserPrompt.isPending}
        />
      )}
    </BotPageLayout>
  );
};

interface UserPromptModalProps {
  member: DiscordMember;
  prompt?: { text: string | null; isEnabled: boolean; createdAt?: string | null; updatedAt?: string | null };
  promptText: string;
  isEnabled: boolean;
  history: Array<{ text: string; createdAt: string | null }>;
  historyLoading: boolean;
  showHistory: boolean;
  isSaving: boolean;
  onChangePrompt: (value: string) => void;
  onToggleEnabled: (value: boolean) => void;
  onToggleHistory: (value: boolean) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}

const UserPromptModal: React.FC<UserPromptModalProps> = ({
  member,
  prompt,
  promptText,
  isEnabled,
  history,
  historyLoading,
  showHistory,
  isSaving,
  onChangePrompt,
  onToggleEnabled,
  onToggleHistory,
  onSave,
  onDelete,
  onClose,
}) => {
  return (
    <Dialog open={!!member} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-panel-strong/60 backdrop-blur-3xl border-stroke p-0 overflow-hidden rounded-[2.5rem] shadow-[0_0_80px_rgba(0,0,0,0.5)]">
        <div className="relative p-8 pb-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-5">
              <div className="relative">
                <img
                  src={getDiscordAvatarUrl(member.id, member.avatar, 128)}
                  alt={member.username}
                  className="w-16 h-16 rounded-[1.5rem] border-2 border-stroke object-cover shadow-2xl transition-transform hover:scale-110"
                />
                <div className={clsx(
                  "absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-4 border-bg-solid shadow-sm",
                  isEnabled ? "bg-green" : "bg-muted"
                )} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-text tracking-tight leading-tight">{member.displayName || member.username}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] font-bold text-muted/40 uppercase tracking-widest">@{member.username}</span>
                  <Badge variant="outline" className="text-[9px] border-stroke px-1.5 py-0 opacity-40 uppercase font-bold">{member.id}</Badge>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 text-muted/40 hover:text-text hover:bg-white/5 rounded-full"
              onClick={onClose}
            >
              <X size={24} />
            </Button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between px-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-muted/60">
                Custom Prompt
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 cursor-pointer group" onClick={() => onToggleEnabled(!isEnabled)}>
                  <span className={clsx("text-[10px] font-bold tracking-widest uppercase transition-colors", isEnabled ? "text-green" : "text-muted/40")}>
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button className={clsx("p-1 rounded-lg transition-colors", isEnabled ? "bg-green/10 text-green" : "bg-panel-soft text-muted")}>
                    {isEnabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="relative group">
              <textarea
                className="w-full bg-panel border border-stroke rounded-3xl p-6 text-sm font-medium text-text placeholder:text-muted/20 focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent/40 resize-none transition-all shadow-inner leading-relaxed min-h-[300px]"
                value={promptText}
                onChange={(e) => onChangePrompt(e.target.value)}
                placeholder="Enter a custom prompt for this user..."
              />
              <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-accent/20 group-focus-within:bg-accent group-focus-within:animate-pulse transition-colors" />
            </div>

            <div className="flex items-center justify-between text-[10px] font-bold tracking-widest text-muted/30 px-2">
              <div className="flex flex-col gap-1">
                {prompt?.updatedAt && <span>Updated: {new Date(prompt.updatedAt).toLocaleTimeString()}</span>}
                {prompt?.createdAt && <span>Created: {new Date(prompt.createdAt).toLocaleDateString()}</span>}
              </div>
              {prompt && (
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-8 bg-red/10 text-red border border-red/20 opacity-60 hover:opacity-100 font-black text-[9px] uppercase tracking-widest rounded-lg"
                  onClick={onDelete}
                >
                  Delete
                </Button>
              )}
            </div>

            <div className="border-t border-stroke/40 pt-6">
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-white/5 hover:bg-white/10 rounded-2xl border border-stroke/40 transition-all group"
                onClick={() => onToggleHistory(!showHistory)}
              >
                <div className="flex items-center gap-3">
                  <History size={16} className="text-accent/60" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted/60 group-hover:text-text transition-colors">Edit History</span>
                </div>
                <ChevronDown
                  size={16}
                  className={clsx("transition-transform duration-300 text-muted/40 group-hover:text-text", showHistory && "rotate-180")}
                />
              </button>

              {showHistory && (
                <div className="mt-4 space-y-3 animate-in slide-in-from-top-2 duration-300">
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-8 text-muted gap-3 animate-pulse">
                      <Loader2 size={16} className="animate-spin text-accent" />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Loading...</span>
                    </div>
                  ) : history.length === 0 ? (
                    <div className="py-8 text-center text-muted text-[10px] font-bold uppercase tracking-widest opacity-30">
                      No history available
                    </div>
                  ) : (
                    history.map((entry, idx) => (
                      <div key={idx} className="bg-panel/40 border border-stroke/40 rounded-2xl p-4 shadow-lg group/entry hover:border-accent/20 transition-all">
                        <div className="text-[9px] font-black uppercase tracking-widest text-muted/30 mb-2">
                          {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : 'Unknown'}
                        </div>
                        <div className="text-xs font-semibold text-text/60 italic leading-relaxed line-clamp-2 group-hover/entry:line-clamp-none transition-all">
                          "{entry.text}"
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-8 bg-panel-strong/40 border-t border-stroke flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            className="h-12 px-8 rounded-2xl text-[11px] font-black uppercase tracking-widest text-muted/60 hover:text-text"
            onClick={onClose}
            disabled={isSaving}
          >
            Abort
          </Button>
          <Button
            variant="ghost"
            className="h-12 px-10 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-accent/20 !bg-accent !text-black !border-none hover:!bg-accent/90"
            onClick={onSave}
            disabled={isSaving || !promptText.trim()}
          >
            {isSaving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles className="mr-2" size={16} />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


const AccordionRow: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title,
  defaultOpen = false,
  children
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-stroke bg-panel-strong/70">
      <Button
        variant="ghost"
        className="w-full text-left p-3 text-sm text-text flex justify-between items-center"
        onClick={() => setOpen(!open)}
      >
        <span>{title}</span>
        <span className="text-xs text-muted">{open ? 'Hide' : 'Show'}</span>
      </Button>
      {open && <div className="p-3">{children}</div>}
    </Card>
  );
};

const ImagePreview: React.FC<{ imageUrl: string; onExpand?: (url: string) => void }> = ({ imageUrl, onExpand }) => (
  <div className="relative aspect-video bg-panel-strong rounded-md overflow-hidden border border-stroke cursor-pointer" onClick={() => onExpand?.(imageUrl)}>
    <img src={imageUrl} alt="PKB event" className="w-full h-full object-cover transition hover:scale-105" />
    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition" />
  </div>
);
